import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Select, SelectItem, Spinner } from "@heroui/react";
import FundPickerBar from "../components/FundPickerBar";
import { useS2DashboardContext } from "../context/S2DashboardProvider";
import { api } from "../services/api";
import { CAPITAL_ACCOUNT_ROWS } from "../model/capitalAccountRows";
import { S2_GROUP_HEADER_CLASS } from "../model/constants";
import { sortPeriods } from "../model/utils";
import PDFModal, { extractPage } from "../components/PDFModal";
import { DataTable } from "@/components/data-table";
import type { CARow, DocMeta } from "../model/types";

// ─── Display row type ─────────────────────────────────────────────────────────

type CADisplayRow = {
  _type: "section" | "data";
  label: string;
  fundCell: React.ReactNode;
  shareCell: React.ReactNode;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCurrency(row: CARow, key: string): string {
  const curr = row[`${key}_curr`];
  if (curr && curr !== "nan" && curr.trim()) return curr.trim();
  return "";
}

function formatVal(
  row: CARow,
  key: string,
  isNum?: boolean,
  isPct?: boolean,
  suffix?: string,
): string {
  const raw = row[`${key}_a`] ?? row[`${key}_val`] ?? "";
  if (!raw || raw === "nan" || raw === "" || raw === "None") return "—";
  if (isNum) {
    const n = parseFloat(String(raw).replace(/,/g, ""));
    if (!isNaN(n)) {
      const curr = getCurrency(row, key);
      const formatted = new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
      }).format(n);
      if (curr) return `${formatted} ${curr}`;
      if (suffix) return `${formatted} ${suffix}`;
      return formatted;
    }
  }
  if (isPct) return raw;
  return raw;
}

function buildPeriodMap(
  docIds: string[],
  docMeta: Record<string, DocMeta>,
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const docId of docIds) {
    const meta = docMeta[docId];
    if (!meta) continue;
    // Some backends do not populate has_ca consistently; only skip documents
    // that are explicitly marked as empty for CA.
    const hasNoCapitalAccountData =
      meta.has_ca === false &&
      (meta.active_holdings === null || meta.active_holdings === 0);
    if (hasNoCapitalAccountData) continue;
    const period = (meta.reporting_period || docId).trim();
    if (!map.has(period)) map.set(period, []);
    map.get(period)!.push(docId);
  }
  return map;
}

function getDocumentLabel(
  meta: DocMeta | undefined,
  docId: string,
  fundName?: string | null,
): string {
  const parts = [
    fundName?.trim(),
    meta?.doc_type?.trim(),
    meta?.reporting_period?.trim(),
  ].filter(Boolean);

  if (parts.length > 0) return parts.join(" - ");

  return meta?.doc_name?.trim() || meta?.file_name?.trim() || docId;
}

// ─── Column definitions ───────────────────────────────────────────────────────

function buildCAColumns(): ColumnDef<CADisplayRow>[] {
  return [
    {
      id: "label",
      header: "Field",
      size: 224,
      cell: ({ row }) => {
        if (row.original._type === "section") {
          return (
            <span className="text-xs font-bold uppercase tracking-wide text-white">
              {row.original.label}
            </span>
          );
        }
        return (
          <span className="text-xs text-gray-700">{row.original.label}</span>
        );
      },
    },
    {
      id: "fund_total",
      header: "Total Fund (100%)",
      size: 192,
      meta: { className: "text-right" },
      cell: ({ row }) => {
        if (row.original._type === "section") return null;
        return row.original.fundCell;
      },
    },
    {
      id: "kfw_share",
      header: "Share",
      size: 192,
      meta: { className: "text-right" },
      cell: ({ row }) => {
        if (row.original._type === "section") return null;
        return row.original.shareCell;
      },
    },
  ];
}

// ─── Row-class callback ───────────────────────────────────────────────────────

function caRowClass(
  row: import("@tanstack/react-table").Row<CADisplayRow>,
): string | undefined {
  if (row.original._type === "section")
    return "bg-[#4a6fa5] hover:bg-[#4a6fa5]";
  return "hover:bg-blue-50";
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function CapitalAccountView() {
  const [grounding, setGrounding] = useState<{
    docId: string;
    fileName?: string;
    page: number;
    quote: string;
  } | null>(null);

  const {
    selectedFundId,
    selectedFund,
    docMeta,
    caSelectedPeriod,
    setCASelectedPeriod,
    caSelectedDocId,
    setCASelectedDocId,
  } = useS2DashboardContext();

  const docIds = selectedFund?.doc_ids ?? [];

  const periodMap = useMemo(
    () => buildPeriodMap(docIds, docMeta),
    [docIds, docMeta],
  );
  const periods = useMemo(
    () => sortPeriods(Array.from(periodMap.keys())),
    [periodMap],
  );

  const effectivePeriod =
    caSelectedPeriod ?? periods[periods.length - 1] ?? null;
  const periodDocIds = effectivePeriod
    ? (periodMap.get(effectivePeriod) ?? [])
    : [];

  const sourceOptions = useMemo(() => {
    return periodDocIds.map((dId) => ({
      key: dId,
      label: getDocumentLabel(docMeta[dId], dId, selectedFund?.fund_name),
    }));
  }, [periodDocIds, docMeta, selectedFund]);

  const effectiveDocId = useMemo(() => {
    if (!caSelectedDocId) return periodDocIds[0] ?? null;
    if (!periodDocIds.includes(caSelectedDocId)) return periodDocIds[0] ?? null;
    return caSelectedDocId;
  }, [caSelectedDocId, periodDocIds]);

  const { data: caRows = [], isLoading } = useQuery({
    queryKey: ["ca", selectedFundId, effectiveDocId],
    queryFn: () => api.getCapitalAccount(selectedFundId!, effectiveDocId),
    enabled: !!selectedFundId && !!effectiveDocId,
    staleTime: 30_000,
  });

  const rawRow: CARow | null = caRows[0] ?? null;
  const displayRow = rawRow;

  const columns = useMemo(() => buildCAColumns(), []);

  // ── Build display rows from CAPITAL_ACCOUNT_ROWS config ─────────────────────
  const tableRows = useMemo<CADisplayRow[]>(() => {
    return CAPITAL_ACCOUNT_ROWS.map((rowCfg): CADisplayRow => {
      if (rowCfg.type === "section") {
        return {
          _type: "section",
          label: rowCfg.label,
          fundCell: null,
          shareCell: null,
        };
      }

      const keyF = rowCfg.keyF!;
      const keyS = rowCfg.keyS;

      if (!displayRow) {
        return {
          _type: "data",
          label: rowCfg.label,
          fundCell: <span className="text-gray-300">—</span>,
          shareCell: <span className="text-gray-300">—</span>,
        };
      }

      // Single-source mode
      if (rawRow) {
        const fDisplay = formatVal(
          rawRow,
          keyF,
          rowCfg.isNum,
          rowCfg.isPct,
          rowCfg.suffix,
        );
        const sDisplay =
          !rowCfg.isSingle && keyS
            ? formatVal(rawRow, keyS, rowCfg.isNum, rowCfg.isPct, rowCfg.suffix)
            : "";
        const fq = rawRow[`${keyF}_q`] ?? "";
        const sq = (keyS ? rawRow[`${keyS}_q`] : "") ?? "";
        const rawDocId = rawRow["system_identifiers_doc_id"] ?? effectiveDocId;
        const fileName = getDocumentLabel(
          docMeta[rawDocId],
          rawDocId,
          selectedFund?.fund_name,
        );

        const makeSourceBtn = (quote: string) =>
          quote ? (
            <button
              onClick={(e) => {
                (e.currentTarget as HTMLButtonElement).blur();
                setGrounding({
                  docId: rawDocId,
                  fileName,
                  page: extractPage(quote),
                  quote,
                });
              }}
              className="shrink-0 text-blue-400 opacity-0 group-hover:opacity-100 hover:text-blue-700 transition-all ml-1 inline-flex"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </button>
          ) : null;

        return {
          _type: "data",
          label: rowCfg.label,
          fundCell: (
            <span className="inline-flex items-center justify-end gap-1">
              <span>{fDisplay}</span>
              {makeSourceBtn(fq)}
            </span>
          ),
          shareCell: rowCfg.isSingle ? (
            <span className="text-gray-300">—</span>
          ) : (
            <span className="inline-flex items-center justify-end gap-1">
              <span>{sDisplay}</span>
              {makeSourceBtn(sq)}
            </span>
          ),
        };
      }

      return {
        _type: "data",
        label: rowCfg.label,
        fundCell: <span className="text-gray-300">—</span>,
        shareCell: <span className="text-gray-300">—</span>,
      };
    });
    // idx used only as key in old code; CAPITAL_ACCOUNT_ROWS is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRow, rawRow, docMeta, effectiveDocId, selectedFund, setGrounding]);

  return (
    <div className="flex flex-col gap-4">
      <FundPickerBar mode="single" />

      {!selectedFundId ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      ) : (
        <>
          {/* Controls */}
          <div className="flex items-center gap-3 flex-wrap">
            {periods.length > 0 && (
              <div className="w-40">
                <Select
                  label="Select period"
                  size="sm"
                  selectedKeys={
                    effectivePeriod ? new Set([effectivePeriod]) : new Set()
                  }
                  onSelectionChange={(keys) => {
                    setCASelectedPeriod(Array.from(keys)[0] as string);
                    setCASelectedDocId(null);
                  }}
                  aria-label="Select reporting period"
                >
                  {periods.map((p) => (
                    <SelectItem key={p}>{p}</SelectItem>
                  ))}
                </Select>
              </div>
            )}

            {sourceOptions.length > 1 && (
              <div className="w-full max-w-[720px] min-w-[420px]">
                <Select
                  label="Select document"
                  size="sm"
                  selectedKeys={new Set([effectiveDocId])}
                  onSelectionChange={(keys) =>
                    setCASelectedDocId(Array.from(keys)[0] as string)
                  }
                  aria-label="Select document"
                  classNames={{
                    value: "truncate",
                    popoverContent: "max-w-[900px]",
                    listboxWrapper: "max-h-80",
                  }}
                >
                  {sourceOptions.map((o) => (
                    <SelectItem key={o.key}>{o.label}</SelectItem>
                  ))}
                </Select>
              </div>
            )}

            {isLoading && <Spinner size="sm" />}
          </div>

          {/* Table */}
          {!displayRow && !isLoading ? (
            <p className="text-sm text-gray-400">No data for this selection.</p>
          ) : (
            !isLoading && (
              <div className="max-w-2xl">
                <DataTable<CADisplayRow, unknown>
                  table_name="pe-capital-account"
                  columns={columns}
                  data={tableRows}
                  pinnedLeft={["label"]}
                  getRowClassName={caRowClass}
                  groupHeaderClassName={S2_GROUP_HEADER_CLASS}
                  tableClassName="w-full"
                  pageSize={1000}
                />
              </div>
            )
          )}
        </>
      )}

      <PDFModal
        isOpen={!!grounding}
        onClose={() => setGrounding(null)}
        docId={grounding?.docId ?? ""}
        fileName={grounding?.fileName}
        page={grounding?.page ?? 1}
        quote={grounding?.quote ?? ""}
      />
    </div>
  );
}
